import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Command, Radio, Users2, Target, Server, Globe, Mail, Users, IdCard, Landmark, LayoutDashboard,
  FolderCheck, Inbox, LineChart, Settings, LogOut, Menu, X, Scale, SlidersHorizontal, Radar, Newspaper, Telescope, Gavel, ShieldAlert, Images, Cpu, Wallet, TrendingUp, BookOpen, Plug, Bot, Receipt,
} from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { DataProvider } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";

const NAV_GROUPS = [
  { section: "Command", items: [
    { to: "/owner", label: "Command Center", icon: Command, end: true },
    { to: "/owner/ceo", label: "CEO Overview", icon: LayoutDashboard },
    { to: "/owner/revenue-intel", label: "Revenue Intelligence", icon: LineChart },
  ]},
  { section: "Growth & Providers", items: [
    { to: "/owner/provider-command", label: "Provider Command", icon: Plug },
    { to: "/owner/approvals-center", label: "Approvals Center", icon: Inbox },
    { to: "/owner/providers", label: "Integrations Factory", icon: Plug },
    { to: "/owner/competitors", label: "Competitor Watch", icon: Telescope },
  ]},
  { section: "AI Workforce", items: [
    { to: "/owner/ai-workforce", label: "AI Workforce Command", icon: Bot },
    { to: "/owner/control-room", label: "AI Control Room", icon: Radio },
    { to: "/owner/employees", label: "The Capital Crew", icon: Users2 },
    { to: "/owner/missions", label: "Missions", icon: Target },
    { to: "/owner/operating-mode", label: "Operating Mode", icon: SlidersHorizontal },
    { to: "/owner/situations", label: "Situations & Mandates", icon: Radar },
    { to: "/owner/ai-governance", label: "AI Governance", icon: Gavel },
    { to: "/owner/ai-safety", label: "AI Safety", icon: ShieldAlert },
    { to: "/owner/ai-models", label: "ChatGPT Models", icon: Cpu },
  ]},
  { section: "Studio & Content", items: [
    { to: "/owner/content", label: "Content Studio", icon: Newspaper },
    { to: "/owner/learning", label: "Learning & Opportunities", icon: BookOpen },
    { to: "/owner/image-studio", label: "Image Studio", icon: Images },
    { to: "/owner/websites", label: "Websites & Funnels", icon: Globe },
    { to: "/owner/marketing", label: "Marketing & Email", icon: Mail },
  ]},
  { section: "Customers & Finance", items: [
    { to: "/owner/customers", label: "Customers & CRM", icon: Users },
    { to: "/owner/credit", label: "Credit Operations", icon: IdCard },
    { to: "/owner/credit-builder", label: "Credit Builder", icon: TrendingUp },
    { to: "/owner/rent-bill", label: "Rent & Bill Queue", icon: Receipt },
    { to: "/owner/memberships", label: "Membership & Entitlements", icon: IdCard },
    { to: "/owner/finance", label: "Finance & Funding", icon: Landmark },
    { to: "/owner/finops", label: "FinOps / Cost", icon: Wallet },
  ]},
  { section: "Ops & Governance", items: [
    { to: "/owner/integrations", label: "Infrastructure", icon: Server },
    { to: "/owner/legal", label: "Legal & Compliance", icon: Scale },
    { to: "/owner/documents", label: "Documents & Applications", icon: FolderCheck },
    { to: "/owner/approvals", label: "Approvals", icon: Inbox },
    { to: "/owner/activity", label: "Activity & Reports", icon: LineChart },
    { to: "/owner/settings", label: "Settings & Security", icon: Settings },
  ]},
];

function NavItems({ onNavigate }) {
  return (
    <nav className="sidebar-scroll flex-1 space-y-5 overflow-y-auto px-3 py-4">
      {NAV_GROUPS.map((group) => (
        <div key={group.section} className="space-y-1">
          <div className="px-3 pb-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-[#5B4BE6]">{group.section}</div>
          {group.items.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate} data-testid={`owner-nav-${item.to.replace(/\//g, "-")}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] font-medium transition-colors ${
                  isActive ? "bg-[#F1F0FB] text-[#4338CA]" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
              <item.icon size={17} className="shrink-0" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

function Inner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileNav, setMobileNav] = useState(false);
  const first = (user?.name || "Owner").split(" ")[0];

  return (
    <div className="font-inter flex h-screen overflow-hidden bg-[#F8FAFC] text-slate-700" data-testid="owner-portal">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-slate-200 bg-white lg:flex">
        <div className="flex h-16 items-center border-b border-slate-200 px-5"><Logo variant="dark" height={22} /></div>
        <NavItems />
        <div className="border-t border-slate-200 p-3">
          <div className="mb-1 px-2 text-[11px] text-slate-400">{user?.email}</div>
          <button onClick={logout} data-testid="owner-logout" className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-[13px] text-slate-600 hover:bg-slate-50 hover:text-slate-900"><LogOut size={15} /> Sign out</button>
        </div>
      </aside>

      {mobileNav && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileNav(false)} />
          <div className="absolute left-0 top-0 flex h-full w-64 flex-col bg-white text-slate-700">
            <div className="flex h-16 items-center justify-between border-b border-slate-200 px-5"><Logo variant="dark" height={22} /><button onClick={() => setMobileNav(false)}><X size={20} className="text-slate-400" /></button></div>
            <NavItems onNavigate={() => setMobileNav(false)} />
          </div>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white/90 px-4 py-4 backdrop-blur-xl sm:px-8" data-testid="owner-shell-header">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileNav(true)} className="lg:hidden"><Menu size={22} className="text-slate-500" /></button>
            <div>
              <div className="font-head text-2xl font-extrabold tracking-tight text-[#0F172A]">Owner <span className="text-[#5B4BE6]">Command Center</span></div>
              <div className="text-[13px] text-slate-500">{first} · Owner</div>
            </div>
          </div>
          <button onClick={() => navigate("/owner/preview")} data-testid="owner-nav--owner-preview"
            className="rounded-xl border border-[#5B4BE6] px-4 py-2 text-[13px] font-bold text-[#5B4BE6] transition-colors hover:bg-[#F1F0FB]">
            Preview Customer View
          </button>
        </header>
        <main className="relative flex-1 overflow-y-auto">
          <div className="relative mx-auto max-w-6xl px-4 py-7 sm:px-8"><Outlet /></div>
        </main>
      </div>
    </div>
  );
}

export function OwnerShell() {
  return (
    <DataProvider>
      <Inner />
    </DataProvider>
  );
}
